/**
 * A client's name, as a link to its own page — `studio/clients.html`'s
 * `<a class="b linkish" href="client-detail.html">BuilderStock</a>`.
 *
 * Four studio screens print a client name and every one of them links it: the book,
 * the money page's run table and ledger, the board's column heads, and the task
 * detail. The link is the same and the sub-line under it is not, which is why the
 * sub-line is `children` rather than a prop with four shapes.
 *
 * `asChild` and a router `Link`, per CONTRACT.md §Internal links: `Linkish` styles,
 * TanStack routes, and a full page load in an internal portal is the thing that rule
 * exists to prevent.
 */

import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { B, Linkish, Muted, Xs } from '../portal/Text.js'

export type ClientLinkProps = {
  /** The slug. `/studio/clients/$slug` is where it goes. */
  readonly client: string
  /** The company as everybody says it — "Vela Interiors", not the slug. */
  readonly name: string
  /** The `.xs.muted` line under it: "Since 6 Jan 2026 · 7 blocks". */
  readonly children?: ReactNode
  /**
   * A cancelled client is printed in muted and **is not a link**.
   *
   * `studio/clients.html`'s last row is Halcyon Coffee in `.muted` with no anchor at
   * all — which is the mockup's own way of saying there is nothing live to open. The
   * page still has a detail route for them, so this is a deliberate choice by the
   * caller rather than a rule this component decides.
   */
  readonly muted?: boolean
}

export function ClientLink({ client, name, children, muted }: ClientLinkProps) {
  const label = muted === true ? <B>{name}</B> : <ClientAnchor client={client} name={name} />

  return (
    <>
      {muted === true ? <Muted>{label}</Muted> : label}
      {children === undefined ? null : (
        /* A line of its own — `studio/clients.html` draws it as `<div class="xs muted">`. */
        <Xs block>
          <Muted>{children}</Muted>
        </Xs>
      )}
    </>
  )
}

function ClientAnchor({ client, name }: { client: string; name: string }) {
  return (
    <Linkish asChild strong>
      <Link to="/studio/clients/$slug" params={{ slug: client }}>
        {name}
      </Link>
    </Linkish>
  )
}
